import update from "immutability-helper";
import { merge, map, mapKeys, camelCase } from "lodash";
import { normalize, denormalize } from "normalizr";
import { Map } from "immutable";

import { Model, NormalisedResult } from "@orm/models";
import { snakeCaseToCamelCase } from "@orm/functions";
import {
    Report,
    IReport,
    NReport,
    ReportType,
    IReportType,
    NReportType,
    ReportStatus,
    IReportStatus,
    ReportsNormalisedEntities,
} from "@reports/models";
import { Chart, IChart, ChartsNormalisedEntities } from "@charts/models";
import { Dsp, IDsp, DspsNormalisedEntities } from "@dsps/models";
import {
    Album,
    IAlbum,
    Track,
    ITrack,
    Artist,
    IArtist,
    Genre,
    IGenre,
    Label,
    ILabel,
    AlbumFormat,
    IAlbumFormat,
    AlbumStatus,
    IAlbumStatus,
    AlbumTrack,
    IAlbumTrack,
    ArtistPivot,
    IArtistPivot,
} from "@music/models";
import {
    Image,
    IImage,
    File,
    IFile,
    FileFormat,
    IFileFormat,
} from "@files/models";
import {
    User,
    IUser,
    Organisation,
    IOrganisation,
    Role,
    IRole,
    Permission,
    IPermission,
    AuthNormalisedEntities,
} from "@auth/models";
import {
    Delivery,
    IDelivery,
    Batch,
    IBatch,
    Type,
    IType,
    StatusType,
    IStatusType,
    Status,
    IStatus,
    DeliveryNormalisedEntities,
} from "@deliveries/models";
import {
    Bundle,
    IBundle,
    BundleStatus,
    IBundleStatus,
    UploadNormalisedEntities,
} from "@upload/models";
import {
    Playlist,
    IPlaylist,
    PlaylistSnapshot,
    IPlaylistSnapshot,
    Track as VendorTrack,
    ITrack as IVendorTrack,
    PlaylistSnapshotTrack,
    IPlaylistSnapshotTrack,
    PlaylistTrack,
    IPlaylistTrack,
    VendorNormalisedEntities,
} from "@vendor/models";
import { Country, ICountry, LocalisationNormalisedEntities } from "@localisation/models";
import {
    NCountry,
    NDsp,
    NTrack,
    NAlbum,
    NArtist,
    NChart,
    DChart,
    NUser,
    NOrganisation,
    NRole,
    NDelivery,
    NBundle,
    NPlaylist,
    NVendorTrack,
    NPlaylistTrends,
    NVendorPlaylistTrack,
    NormalisedEntities,
} from "./models";

type ModelNormalisedEntities = AuthNormalisedEntities & ChartsNormalisedEntities &
    LocalisationNormalisedEntities & DeliveryNormalisedEntities &
    DspsNormalisedEntities & ReportsNormalisedEntities & UploadNormalisedEntities &
    VendorNormalisedEntities;

type Key = string | number;

interface EntityMap {
    [key: string]: any;
    [key: number]: any;
}

export class ApiHelper {
    /**
     * Function to deserialise json strings, converting dates.
     *
     * @param string data
     * @return any
     */
    public static deserialize(data: string): any {
        return JSON.parse(data, ApiHelper.reviveDateTime);
    }

    /**
     * Function to normalise the data.
     *
     * @param kind string
     * @param data ApiModel | ApiModel[]
     * @return something
     */
    public static normaliseData(kind: string, data: any) {
        const normalisedMap: {[key: string]: any} = {
            "organisation": NOrganisation,
            "role": NRole,
            "album": NAlbum,
            "track": NTrack,
            "artist": NArtist,
            "country": NCountry,
            "delivery": NDelivery,
            "dsp": NDsp,
            "report": NReport,
            "report-type": NReportType,
            "bundle": NBundle,
            "playlist": NPlaylist,
            "vendor-track": NVendorTrack,
            "vendor-playlist-track": NVendorPlaylistTrack,
            "playlist-trends": NPlaylistTrends,
        };

        if (kind === "user") {
            // Function to map permissions levels onto users.
            const mapPermissionsToUser = (user: any) => {
                user.permissionsLevels = {};
                for (const role of user.roles) {
                    for (const permission of role.permissions) {
                        if (permission.pivot != null && permission.pivot.level_name != null) {
                            const level = permission.pivot.level_name;
                            if (user.permissionsLevels[permission.id] === undefined &&
                                ["system", "organisation", "user"].indexOf(level) > -1) {
                                user.permissionsLevels[permission.id] = level;
                            }
                            else if (level === "system") {
                                user.permissionsLevels[permission.id] = level;
                            }
                            else if (level === "organisation" &&
                                user.permissionsLevels[permission.id] === "user") {
                                user.permissionsLevels[permission.id] = level;
                            }
                        }
                    }
                }

                return user;
            };

            let normalizedData = null;
            if (Array.isArray(data)) {
                // Loop through and place the permissionsLevels on the user.
                for (let i = 0; i < data.length; i++) {
                    data[i] = mapPermissionsToUser(data[i]);
                }
                normalizedData = normalize(data, [NUser]);
            }
            else {
                // Place the permissionsLevels on the user.
                data = mapPermissionsToUser(data);
                normalizedData = normalize(data, NUser);
            }
            return normalizedData as NormalisedResult<string | number, NormalisedEntities>;
        }
        else if (kind === "chart") {
            let normalizedData = null;
            let schema = null;
            if (Array.isArray(data)) {
                schema = [DChart];
                normalizedData = normalize(data, [NChart]);
            }
            else {
                schema = DChart;
                normalizedData = normalize(data, NChart);
            }

            // Once that's done we need to denormalize the charts and rows.
            const denormalised = denormalize(
                {charts: normalizedData.result},
                {charts: schema},
                normalizedData.entities,
            );
            if (Array.isArray(normalizedData.result)) {
                for (const chart of denormalised.charts) {
                    normalizedData.entities.charts[chart.name] = chart;
                }
            }
            else {
                normalizedData.entities.charts[denormalised.charts.name] = denormalised.charts;
            }
            delete normalizedData.entities.chartRows;

            return normalizedData as NormalisedResult<number, NormalisedEntities>;
        }
        else if (normalisedMap.hasOwnProperty(kind)) {
            let normalizedData = null;
            if (Array.isArray(data)) {
                normalizedData = normalize(data, [normalisedMap[kind]]);
            }
            else {
                normalizedData = normalize(data, normalisedMap[kind]);
            }
            return normalizedData as NormalisedResult<number | string, NormalisedEntities>;
        }

        // Return the unnormalised data.
        return data;
    }

    /**
     * Merge multiple normalised entities models.
     *
     * @param NormalisedEntities base
     * @param NormalisedEntities[] ...entities
     * @return NormalisedEntities
     */
    public static mergeNormalisedEntities(base: NormalisedEntities, ...entities: NormalisedEntities[]) {
        const merged: any = {...base};
        for (const e of entities) {
            const eMap = Map<string, any>(e);
            eMap.map((prop, key) => {
                if (merged.hasOwnProperty(key)) {
                    merged[key] = merge(merged[key], prop);
                }
                else {
                    merged[key] = prop;
                }
            });

        }
        return merged as NormalisedEntities;
    }

    /**
     * Function to map a given json object to a model.
     *
     * @param string kind
     * @param Object jsonObj
     * @return Model
     */
    public static mapJsonToModel(kind: string, jsonObj: any): NormalisedResult<any, ModelNormalisedEntities> {
        let model: NormalisedResult<any, ModelNormalisedEntities> = null;

        // Fill in the data for recognised types
        if (kind === "user" || kind === "organisation" || kind === "role" ||
            kind === "chart" || kind === "album" || kind === "track" ||
            kind === "country" || kind === "delivery" || kind === "dsp" ||
            kind === "report" || kind === "report-type" || kind === "bundle" ||
            kind === "playlist" || kind === "vendor-track" || kind === "vendor-playlist-track" ||
            kind === "playlist-trends" || kind === "artist"
        ) {
            const entities = {} as ModelNormalisedEntities;
            // We're getting normalised data here so convert all the related entities
            // and put in a map.
            const normalised = jsonObj.entities as NormalisedEntities;
            if (normalised.organisations != null) {
                entities.organisations =
                    ApiHelper.convertAndPushModels<IOrganisation, Organisation, number>(
                        normalised.organisations,
                        Organisation,
                    );
            }
            if (normalised.permissions != null) {
                entities.permissions =
                    ApiHelper.convertAndPushModels<IPermission, Permission, number>(
                        normalised.permissions,
                        Permission,
                    );
            }
            if (normalised.roles != null) {
                entities.roles =
                    ApiHelper.convertAndPushModels<IRole, Role, string>(
                        normalised.roles,
                        Role,
                        false,
                    );
            }
            if (normalised.users != null) {
                entities.users =
                    ApiHelper.convertAndPushModels<IUser, User, number>(
                        normalised.users,
                        User,
                    );
            }
            if (normalised.artists != null) {
                entities.artists =
                    ApiHelper.convertAndPushModels<IArtist, Artist, number>(
                        normalised.artists,
                        Artist,
                        true,
                        (e: IArtist) => {
                            delete e.pivot;
                            return e;
                        },
                    );
            }
            if (normalised.artistPivots != null) {
                entities.artistPivots =
                    ApiHelper.convertAndPushModels<IArtistPivot, ArtistPivot, string>(
                        normalised.artistPivots,
                        ArtistPivot,
                        false,
                    );
            }
            if (normalised.tracks != null) {
                entities.tracks =
                    ApiHelper.convertAndPushModels<ITrack, Track, number>(
                        normalised.tracks,
                        Track,
                        true,
                        (e: Track) => {
                            delete e.pivot;
                            return e;
                        },
                    );
            }
            if (normalised.albums != null) {
                entities.albums =
                    ApiHelper.convertAndPushModels<IAlbum, Album, number>(
                        normalised.albums,
                        Album,
                        true,
                        (e: IAlbum) => {
                            delete e.artwork;
                            delete e.format;
                            delete e.status;
                            delete e.genre;
                            delete e.additionalGenre;
                            delete e.organisation;
                            return e;
                        },
                    );
            }
            if (normalised.albumFormats != null) {
                entities.albumFormats =
                    ApiHelper.convertAndPushModels<IAlbumFormat, AlbumFormat, string>(
                        normalised.albumFormats,
                        AlbumFormat,
                        false,
                    );
            }
            if (normalised.albumStatuses != null) {
                entities.albumStatuses =
                    ApiHelper.convertAndPushModels<IAlbumStatus, AlbumStatus, string>(
                        normalised.albumStatuses,
                        AlbumStatus,
                        false,
                    );
            }
            if (normalised.albumTracks != null) {
                entities.albumTracks =
                    ApiHelper.convertAndPushModels<IAlbumTrack, AlbumTrack, string>(
                        normalised.albumTracks,
                        AlbumTrack,
                        false,
                    );
            }
            if (normalised.genres != null) {
                entities.genres =
                    ApiHelper.convertAndPushModels<IGenre, Genre, number>(
                        normalised.genres,
                        Genre,
                    );
            }
            if (normalised.labels != null) {
                entities.labels =
                    ApiHelper.convertAndPushModels<ILabel, Label, number>(
                        normalised.labels,
                        Label,
                    );
            }
            if (normalised.images != null) {
                entities.images =
                    ApiHelper.convertAndPushModels<IImage, Image, number>(
                        normalised.images,
                        Image,
                        true,
                        (e: IImage) => {
                            e.fullsizeFileId = Number(e.fullsizeFile);
                            e.thumbMediumFileId = Number(e.thumbMediumFile);
                            e.thumbSmallFileId = Number(e.thumbSmallFile);
                            delete e.fullsizeFile;
                            delete e.thumbMediumFile;
                            delete e.thumbSmallFile;
                            return e;
                        },
                    );
            }
            if (normalised.files != null) {
                entities.files =
                    ApiHelper.convertAndPushModels<IFile, File, number>(
                        normalised.files,
                        File,
                    );
            }
            if (normalised.fileFormats != null) {
                entities.fileFormats =
                    ApiHelper.convertAndPushModels<IFileFormat, FileFormat, string>(
                        normalised.fileFormats,
                        FileFormat,
                        false,
                    );
            }
            if (normalised.charts != null) {
                entities.charts =
                    ApiHelper.convertAndPushModels<IChart, Chart, string>(
                        normalised.charts,
                        Chart,
                        false,
                        (transformed: IChart) => {
                            // Covert the rows as they're not normalised
                            transformed.rows = map(transformed.rows, (row) => {
                                const r = ApiHelper.objectFromApiModel(row);
                                delete r.album;
                                delete r.track;
                                return r;
                            });
                            return transformed;
                        },
                    );
            }
            if (normalised.countries != null) {
                entities.countries =
                    ApiHelper.convertAndPushModels<ICountry, Country, string>(
                        normalised.countries,
                        Country,
                        false,
                    );
            }
            if (normalised.dsps != null) {
                entities.dsps =
                    ApiHelper.convertAndPushModels<IDsp, Dsp, number>(
                        normalised.dsps,
                        Dsp,
                    );
            }
            if (normalised.deliveries != null) {
                entities.deliveries =
                    ApiHelper.convertAndPushModels<IDelivery, Delivery, string>(
                        normalised.deliveries,
                        Delivery,
                        false,
                    );
            }
            if (normalised.deliveryBatches != null) {
                entities.deliveryBatches =
                    ApiHelper.convertAndPushModels<IBatch, Batch, string>(
                        normalised.deliveryBatches,
                        Batch,
                        false,
                    );
            }
            if (normalised.deliveryTypes != null) {
                entities.deliveryTypes =
                    ApiHelper.convertAndPushModels<IType, Type, string>(
                        normalised.deliveryTypes,
                        Type,
                        false,
                    );
            }
            if (normalised.deliveryStatusTypes != null) {
                entities.deliveryStatusTypes =
                    ApiHelper.convertAndPushModels<IStatusType, StatusType, string>(
                        normalised.deliveryStatusTypes,
                        StatusType,
                        false,
                    );
            }
            if (normalised.deliveryStatuses != null) {
                entities.deliveryStatuses =
                    ApiHelper.convertAndPushModels<IStatus, Status, string>(
                        normalised.deliveryStatuses,
                        Status,
                        false,
                    );
            }
            if (normalised.bundles != null) {
                entities.bundles =
                    ApiHelper.convertAndPushModels<IBundle, Bundle, number>(
                        normalised.bundles,
                        Bundle,
                        true,
                        (e: IBundle) => {
                            // Map the attrs to an Album
                            if (e.attrs != null) {
                                e.attrs = new Album(ApiHelper.objectFromApiModel(e.attrs));
                            }
                            return e;
                        },
                    );
            }
            if (normalised.bundleStatuses != null) {
                entities.bundleStatuses =
                    ApiHelper.convertAndPushModels<IBundleStatus, BundleStatus, string>(
                        normalised.bundleStatuses,
                        BundleStatus,
                        false,
                    );
            }
            if (normalised.playlists != null) {
                entities.playlists =
                    ApiHelper.convertAndPushModels<IPlaylist, Playlist, number>(
                        normalised.playlists,
                        Playlist,
                    );
            }
            if (normalised.playlistSnapshots != null) {
                entities.playlistSnapshots =
                    ApiHelper.convertAndPushModels<IPlaylistSnapshot, PlaylistSnapshot, number>(
                        normalised.playlistSnapshots,
                        PlaylistSnapshot,
                    );
            }
            if (normalised.vendorTracks != null) {
                entities.vendorTracks =
                    ApiHelper.convertAndPushModels<IVendorTrack, VendorTrack, number>(
                        normalised.vendorTracks,
                        VendorTrack,
                    );
            }
            if (normalised.playlistTracks != null) {
                entities.playlistTracks =
                    ApiHelper.convertAndPushModels<IPlaylistTrack, PlaylistTrack, string>(
                        normalised.playlistTracks,
                        PlaylistTrack,
                        false,
                    );
            }
            if (normalised.playlistSnapshotTracks != null) {
                entities.playlistSnapshotTracks =
                    ApiHelper.convertAndPushModels<IPlaylistSnapshotTrack, PlaylistSnapshotTrack, string>(
                        normalised.playlistSnapshotTracks,
                        PlaylistSnapshotTrack,
                        false,
                    );
            }
            if (normalised.reports != null) {
                entities.reports =
                    ApiHelper.convertAndPushModels<IReport, Report, number>(
                        normalised.reports,
                        Report,
                    );
            }
            if (normalised.reportTypes != null) {
                entities.reportTypes =
                    ApiHelper.convertAndPushModels<IReportType, ReportType, number>(
                        normalised.reportTypes,
                        ReportType,
                    );
            }
            if (normalised.reportStatuses != null) {
                entities.reportStatuses =
                    ApiHelper.convertAndPushModels<IReportStatus, ReportStatus, string>(
                        normalised.reportStatuses,
                        ReportStatus,
                        false,
                    );
            }
            model = {
                result: jsonObj.result,
                entities,
            };
            if (kind === "playlist-trends") {
                model.result = map(jsonObj.result, (row) => mapKeys(row, (_v, k) => camelCase(k)));
            }
        }

        return model;
    }

    /**
     * Function to convert an api model to an object with the keys transformed to camel case.
     *
     * @param ApiModel apiModel - The model to fill from
     * @param string[] ignore - The properties to ignore
     * @return any
     */
    public static objectFromApiModel(apiModel: any, ignore: string[] = []): any {
        // Create the object to convert the properties.
        const obj: any = {};

        // Now assign the properties from the apiModel to the other object,
        // converting the names, where relevant.
        const apiModelMap = Map<string, any>(apiModel);
        apiModelMap.map((value, prop) => {
            const camelCaseProp: string = snakeCaseToCamelCase(prop);

            // Ignore anything in the ignore array.
            if (ignore.indexOf(camelCaseProp) === -1) {
                // Loop through arrays.
                if (Array.isArray(value)) {
                    obj[camelCaseProp] = [];
                    for (let i = 0; i < value.length; i++) {
                        if (value[i] && typeof value[i] === "object" && value[i].constructor === Object) {
                            // Recurse
                            obj[camelCaseProp][i] = ApiHelper.objectFromApiModel(value[i]);
                        }
                        else {
                            obj[camelCaseProp][i] = value[i];
                        }
                    }
                }
                else if (value && typeof value === "object" && value.constructor === Object) {
                    // Recurse
                    obj[camelCaseProp] = ApiHelper.objectFromApiModel(value);
                }
                else {
                    obj[camelCaseProp] = value;
                }
            }
        });

        return obj;
    }

    /**
     * Create an entity model from the request, optional extra id.
     *
     * @param string endpoint - The endpoint
     * @param any request - The request
     * @param number id = null - The new id
     * @return Model | User | Report | null
     */
    public static createModelFromRequest(
        endpoint: string,
        request: any,
        id: number = null,
    ): Model | User | Report {

        if (request != null) {
            // If we have an id, it's being created. Set it and the createdAt date.
            const newRequest: any = {};
            if (id != null) {
                newRequest.id = id;
                newRequest.createdAt = new Date();
            }

            // Also set the updatedAt date.
            newRequest.updatedAt = new Date();

            // Merge the requests
            const data = ApiHelper.objectFromApiModel(
                update(request, { $merge: newRequest }),
            );

            if (endpoint === "users") {
                return new User(data);
            }
            else if (endpoint === "reports") {
                return new Report(data);
            }
        }

        return null;
    }

    /**
     * Helper function for mapJsonToModel
     */
    private static convertAndPushModels<I, C, K extends Key>(
        entities: EntityMap,
        type: new(obj: any) => C,
        isNumber: boolean = true,
        transform?: (obj: I) => I,
    ): Map<K, C>
    {
        const tuples = Array<[K, C]>();
        const entitiesMap = Map<K, C>(entities);
        entitiesMap.map((obj, index) => {
            let key: K = index as K;
            if (isNumber) {
                key = Number(index) as K;
            }
            let transformed: I = ApiHelper.objectFromApiModel(obj);
            if (transform != null) {
                transformed = transform(transformed);
            }
            tuples.push([key, new type(transformed)]);
        });
        return Map<K, C>(tuples);
    }

    /**
     * Helper function for JSON.parse to revive DateTime objects.
     *
     * @param any key
     * @param any value
     * @return any
     */
    private static reviveDateTime(_key: any, value: any): any {
        if (typeof value === "string") {
            if (value.match(/^\d{4}-\d{2}-\d{2}/)) {
                const t = value.split(/[- :T+]/);
                return new Date(Date.UTC(
                    Number(t[0]),
                    Number(t[1]) - 1,
                    Number(t[2]),
                    t.length > 3 ? Number(t[3]) : null,
                    t.length > 3 ? Number(t[4]) : null,
                    t.length > 3 ? Number(t[5]) : null,
                ));
            }
        }

        return value;
    }
}
